import React, { useEffect, useState } from 'react'
import { Flex } from 'antd'
import Text from 'shared/components/typography/Text'
import AudioPlayerButton, { AudioPlayerButtonType } from './Button'
import { toHHMMSS } from '@/shared/utils/toHHMMSS.ts'
import { useAudioPlayerContext } from '@/contexts/AudioPlayerContext/AudioPlayerContext'

interface IAudioPlayerProps {
  audioSrc: string
  showDuration?: boolean
  showElapsedTime?: boolean
  buttonType?: AudioPlayerButtonType
}

function AudioPlayer({ audioSrc, showDuration, showElapsedTime, buttonType }: IAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [downloaded, setDownloaded] = useState(false)
  const [audio] = useState(new Audio(audioSrc))

  const { currentAudio, setCurrentAudio } = useAudioPlayerContext()

  const pauseAudio = () => {
    audio.pause()
    audio.currentTime = 0
    setCurrentAudio(null)
  }

  const playAudio = () => {
    if (currentAudio) {
      currentAudio.pause()
      currentAudio.currentTime = 0
    }
    setCurrentAudio(audio)
    audio.play()
  }

  const togglePlayPause = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    if (isPlaying) {
      pauseAudio()
    } else {
      playAudio()
    }
    setIsPlaying(!isPlaying)
  }

  useEffect(() => {
    const onLoadedMetadata = () => {
      setDownloaded(true)
      setCurrentTime(audio.currentTime)
    }

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const onPause = () => {
      setIsPlaying(false)
    }

    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('pause', onPause)

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('pause', onPause)
    }
  }, [audio])

  useEffect(() => {
    return () => {
      pauseAudio()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio])

  return (
    <Flex align="center" gap={4}>
      <AudioPlayerButton onClick={togglePlayPause} type={buttonType} isPlaying={isPlaying} />
      {showDuration && downloaded && (
        <Text type="primary" fontType="body" fontSize={14}>
          {showElapsedTime && toHHMMSS(Math.floor(currentTime)) + ' / '}
          {toHHMMSS(Math.floor(audio.duration))}
        </Text>
      )}
    </Flex>
  )
}

export default AudioPlayer
